import React, { useEffect, useRef, useState } from 'react'
import { Button, Chip, ChipList, ChipProps, ChipRemoveEvent } from '@progress/kendo-react-buttons';
import { axiosWithAuth } from '../../../utils/customAxios';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import CustomInput from '../../../components/custom/form/CustomInput';
import useMasterData from '../../../hooks/useMasterData';
import CustomComboBox from '../../../components/custom/form/CustomComboBox';
import { DeviceAllocation } from '../../../types/device/deviceAllocation';
import deviceService from '../../../services/device.service';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import moment from 'moment';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { Input, InputChangeEvent, RadioButton, RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { CustomCalendar } from '../../../components/custom/form/CustomCalendar';
import { Upload, UploadFileInfo, UploadOnAddEvent, UploadOnProgressEvent, UploadOnRemoveEvent, UploadOnStatusChangeEvent } from '@progress/kendo-react-upload';
import { parseJSON } from '../../../utils/customJsonParser';
import { appUrl } from '../../..';
import { AppLocale } from '../../../contexts/LocaleProvider';
import { SweetAlertOptions } from 'sweetalert2';
import useSwal from '../../../hooks/useSwal';
import { AxiosError } from 'axios';
import { Slide } from '@progress/kendo-react-animation';
import { Notification } from '@progress/kendo-react-notification';
import useTranslation from '../../../hooks/useTranslation';
import useLocale from '../../../hooks/useLocale';
import "./UploadRecording.scss";
import { API_BASE_URL } from '../../../constants';

const CallDirectionValidator=(val:string)=>{
    if(!val){
        return "This field is required"
    }
}

const CallTimeValidator=(val:string)=>{
    if(!val){
        return "This field is required"
    }
}

const FirstPartyValidator=(val:string)=>{
    if(!val){
        return "This field is required"
    }
}

interface OtherPartyProps{
    state: "internal" | "external";
    value:string;
}

interface MediaFile{
    mediaFileReference:string;
}

interface CallCreation{
    mediaFile:MediaFile;
    platformCallId?:string;
    platformCallDetailId?:string;
    firstPartyIdentifier:string;
    otherParties:string[];
    calltimeUtc:string;
    callDirection:string;
    externalSystemIdentifier?:string;
}

const UploadRecording: React.FC = () => {
    const trans = useTranslation("ImportRecording");
    const localeCtx = useLocale();
    const swal = useSwal();
    const masterData = useMasterData();
    const formRef = useRef<any>();
    const [files, setFiles] = React.useState<Array<UploadFileInfo>>([]);
    const [mediaFileReference, setMediaFileReference]=useState<string>("");
    const [loader, setLoader]=useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [devices, setDevices]=useState<DeviceAllocation[] | undefined>(undefined);
    const [otherParties, setOtherParties]=useState<OtherPartyProps[]>([]);
    const [addOtherPartyButtonToggle, setAddOtherPartyButtonToggle] = useState<boolean>(false);
    const [internalExternalRadioButton, setInternalExternalRadioButton] = useState<string>("");
    const [internalPartySelectedForOtherParties, setInternalPartySelectedForOtherParties] = useState<string|undefined>(undefined);
    const [externalPartySelectedForOtherParties, setExternalPartySelectedForOtherParties] = useState<string>("");
    const [notificationToggle, setNotificationToggle] = useState<boolean>(false);

    useEffect(() => {
        if (!localeCtx?.selectedLocale?.current.componentTranslations["ImportRecording"]) {
          trans.fetchTranslations("ImportRecording");
        }
      }, [localeCtx?.selectedLocale]);
     
    useEffect(()=>{
        getDevices();
    },[])

    useEffect(()=>{
        let notificationToggleTimeoutRef: ReturnType<typeof setTimeout>;
        if(notificationToggle){
            notificationToggleTimeoutRef = setTimeout(()=>{
                setNotificationToggle(false);
            },7000)
        }
        return ()=>{
            clearTimeout(notificationToggleTimeoutRef);
        }
    },[notificationToggle])

    const getDevices = async () => {
        try{
            const response = await deviceService.getAllCustomerDevice();
            setDevices(response);
        } catch (err){
            setDevices([]);
        }
    }

    const onAdd = (event: UploadOnAddEvent) => {
        setFiles(event.newState)
    }

    const onRemove = (event: UploadOnRemoveEvent) => {
        setFiles(event.newState);
    }

    const onProgress = (event: UploadOnProgressEvent) => {
        setFiles(event.newState);
    }

    const onStatusChange = (event: UploadOnStatusChangeEvent) => {
        let errMssg = trans.fetchLabelKeyTranslation("FileUploadErr","Cannot upload the file. Please try again later.");
        setNotificationToggle(false);
        setFiles(event.newState);
        if(event.response){
            if(event.response?.response.status && event.response?.response.status !== 200){
                errMssg = event.response?.response.data?.Error ? event.response.response.data?.Error : errMssg;
                setError(errMssg);
            }else{
                setMediaFileReference(event.response?.response.mediaFileReference);
                setError("");
                setNotificationToggle(true);
            }
        }
    }

    const submitHandler = async () => {
        const form = formRef.current as Form;
        const callCreationData : CallCreation={
            mediaFile:{
                mediaFileReference:mediaFileReference,
            },
            platformCallId: form.values.platformCallId,
            platformCallDetailId: form.values.platformCallDetailId,
            firstPartyIdentifier: form.values.firstPartyIdentifier.device.deviceIdentifier,
            otherParties: otherParties.map((op:OtherPartyProps)=>op.value),
            calltimeUtc: moment(form.values.calltimeUtc).format("YYYY-MM-DD HH:mm:ss"),
            callDirection: form.values.callDirection.name,
            externalSystemIdentifier:"ExternalSystemRecorder"
        }
        setLoader(true);
        try{
            await axiosWithAuth.post("/calldetail",callCreationData);
            setFiles([]);
            setOtherParties([]);
            setMediaFileReference("");
            setError("");
            setNotificationToggle(false);
             const swalOptions: SweetAlertOptions<any, any> = {
                icon: "success",
                title: trans.fetchLabelKeyTranslation("CallCreationSuccessMsg","Call has been created successfully."),
                confirmButtonText: trans.fetchLabelKeyTranslation("OkText","OK"),
                };
            swal.fire(swalOptions);
        }catch(err){
            if(err instanceof AxiosError){
                let errMssg = trans.fetchLabelKeyTranslation("CallCreationFailedMsg","Call creation failed. Please try again later.");
                if(err.response?.data && err.response.data.error){
                    errMssg = err.response.data.error;
                }
                setError(errMssg);
            }
        }finally{
            setLoader(false);
        }
    }

    const onCloseDialog=()=>{
        setMediaFileReference("");
        setFiles([]);
        setNotificationToggle(false);
        setError("");
    }

    const onOtherPartyRemove=(event:ChipRemoveEvent)=>{
        const otherParty = event.target.props.dataItem;
        setOtherParties(otherParties.filter((op:OtherPartyProps)=>op.value!==otherParty.value))
    }

    return (
        <div>
            <h1>{trans.fetchLabelKeyTranslation("ImportRecordingTitle","Import Recording")}</h1>
            <div>
                <Upload
                    disabled={mediaFileReference ? true : false}
                    saveField='file'
                    autoUpload={false}
                    batch={false}
                    multiple={false}
                    saveHeaders={{
                        'Content-Type':'multipart-formdata',
                        'Authorization':`Bearer ${parseJSON(localStorage.getItem("VoiceAIToken"))}`,
                        'AppURL':appUrl ?? "",
                        'Locale':parseJSON<AppLocale>(localStorage.getItem("AppLocale"))?.current.locale.code ?? ""
                    }}
                    files={files}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    onProgress={onProgress}
                    onStatusChange={onStatusChange}
                    withCredentials={false}
                    saveUrl={`${API_BASE_URL}/recording`}
                    responseType='json'
                />
            </div>
            {!mediaFileReference && error && (
                <div className='d-flex align-items-center'>
                    <span className='fs-14 tx-red'>{error}</span>
                    <i onClick={()=>setError("")} className="bi bi-x cursor-pointer"></i>
                </div>
            )}
            <div>
                {mediaFileReference && notificationToggle && (
                    <Slide direction={'up'}>
                        <Notification
                            type={{ style: 'success', icon: true }}
                            closable={true}
                            onClose={() => setNotificationToggle(false)}
                        >
                            <span>{trans.fetchLabelKeyTranslation("UploadSuccessMssg","Your file has been uploaded successfully. Please complete the required information below regarding the call.")}</span>
                        </Notification>
                    </Slide>
                )}
            </div>
            {mediaFileReference && (
                <div>
                    <Form
                        ref={formRef}
                        initialValues={{
                            platformCallId: "",
                            platformCallDetailId: "",
                            firstPartyIdentifier: undefined,
                            calltimeUtc: "",
                            callDirection: undefined
                        }}
                        render={(formRenderProps: FormRenderProps) => (
                            <FormElement style={{ maxWidth: 650 }}>
                                <div style={{ marginBottom: "0px", minWidth: "250px" }}>
                                    <div className="formBoxRow p-t-5 p-b-5">
                                        <div className="formBoxLabel fs-14">
                                            {trans.fetchLabelKeyTranslation("FirstPartyField","First Party Identifier")}*
                                        </div>
                                        <div className="formBoxAction">
                                            <div className="formInput">
                                                <Field
                                                id="firstPartyIdentifier"
                                                name="firstPartyIdentifier"
                                                style={{ height: "32px" }}
                                                value={formRenderProps.valueGetter("firstPartyIdentifier")}
                                                component={CustomComboBox}
                                                placeholder={trans.fetchLabelKeyTranslation("FirstPartyPlaceholder","Choose first party")}
                                                data={devices?.filter((_d:DeviceAllocation)=>!otherParties.map((op:OtherPartyProps)=>op.value).includes(_d.device.deviceIdentifier))}
                                                textField="device.deviceIdentifier"
                                                validator={FirstPartyValidator}
                                                filtering={true}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="formBoxRow p-t-5 p-b-5">
                                        <div className="formBoxLabel fs-14">
                                        {trans.fetchLabelKeyTranslation("OtherPartyField","Other Parties")}*
                                            <br/>
                                            <div className="filterChipList">
                                                {otherParties.length > 0 && (
                                                    <ChipList
                                                    data={otherParties}
                                                    selection="single"
                                                    textField='value'
                                                    chip={(props: ChipProps) => (
                                                        <Chip
                                                        {...props}
                                                        size={"large"}
                                                        themeColor={`${
                                                            props.dataItem.state === "internal" ? "success" : "base"
                                                        }`}
                                                        fillMode={"solid"}
                                                        rounded={"full"}
                                                        removable={true}
                                                        onRemove={onOtherPartyRemove}
                                                        />
                                                    )}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        {!addOtherPartyButtonToggle ? (
                                            <Button
                                                style={{fontSize:"10px"}}
                                                className={"k-button k-button-md k-rounded-md k- k-button-solid-base bg-primary text-white"}
                                                onClick={()=>setAddOtherPartyButtonToggle(true)}
                                            >
                                                <i className="bi bi-plus-lg"></i>
                                            </Button>
                                        ):(
                                            <div className="formBoxAction">
                                                <div className="k-d-flex k-align-items-center">
                                                    <div>
                                                        <div className="k-form-field-wrap otherparty-radio-label">
                                                            <RadioButton
                                                                size={"small"}
                                                                name="internalExternalRadioButton"
                                                                value="internal"
                                                                checked={internalExternalRadioButton === 'internal'}
                                                                label={trans.fetchLabelKeyTranslation("RadioBtnInternalText","Internal Party")}
                                                                onChange={(event:RadioButtonChangeEvent)=>setInternalExternalRadioButton(event.value)}
                                                            />
                                                            <br />
                                                            <RadioButton
                                                                size={"small"}
                                                                name="internalExternalRadioButton"
                                                                value="external"
                                                                checked={internalExternalRadioButton === 'external'}
                                                                label={trans.fetchLabelKeyTranslation("RadioBtnExternalText","External Party")}
                                                                onChange={(event:RadioButtonChangeEvent)=>setInternalExternalRadioButton(event.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {internalExternalRadioButton==="internal" && (
                                                    <div className="formInput">
                                                        <CustomComboBox
                                                         style={{ height: "32px" }}
                                                         placeholder={trans.fetchLabelKeyTranslation("InternalPartyPlaceholder","Choose internal party")}
                                                         data={devices?.filter((_d:DeviceAllocation)=>!otherParties.map((op:OtherPartyProps)=>op.value).includes(_d.device.deviceIdentifier) && formRenderProps.valueGetter("firstPartyIdentifier")?.device?.deviceIdentifier!==_d.device.deviceIdentifier)}
                                                         textField={"device.deviceIdentifier"}
                                                         onChange={(event:ComboBoxChangeEvent)=>{
                                                             if(event.value){
                                                                 setInternalPartySelectedForOtherParties(event.value.device.deviceIdentifier);
                                                             }else{
                                                                 setInternalPartySelectedForOtherParties("");
                                                             }
                                                         }}
                                                         filtering={true}
                                                         />
                                                    </div>
                                                )}
                                                {internalExternalRadioButton==="external" && (
                                                    <div className="formInput m-l-5">
                                                        <Input
                                                            style={{ height: "32px" }}
                                                            value={externalPartySelectedForOtherParties}
                                                            placeholder={trans.fetchLabelKeyTranslation("ExternalPartyPlaceholder","Enter external party")}
                                                            onChange={(event:InputChangeEvent)=>{
                                                                setExternalPartySelectedForOtherParties(event.value);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <Button
                                                className={"btn bg-black-5 m-l-5 m-t-5"}
                                                onClick={()=>{
                                                    setAddOtherPartyButtonToggle(false);
                                                    setInternalExternalRadioButton("");
                                                }}
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </Button>
                                                <Button
                                                    className={"k-button k-button-md k-rounded-md k- k-button-solid-base bg-primary text-white m-l-5 m-t-5"}
                                                    disabled={(!internalPartySelectedForOtherParties && !externalPartySelectedForOtherParties) || (formRenderProps.valueGetter("firstPartyIdentifier")?.device?.deviceIdentifier!==undefined && formRenderProps.valueGetter("firstPartyIdentifier")?.device?.deviceIdentifier === internalPartySelectedForOtherParties)}
                                                    onClick={()=>{
                                                        if(internalPartySelectedForOtherParties && !otherParties.map((op:OtherPartyProps)=>op.value).includes(internalPartySelectedForOtherParties)){
                                                            setOtherParties([...otherParties,{
                                                                state:"internal",
                                                                value:internalPartySelectedForOtherParties
                                                            }]);
                                                        }
                                                        if(externalPartySelectedForOtherParties && !otherParties.map((op:OtherPartyProps)=>op.value).includes(externalPartySelectedForOtherParties)){
                                                            setOtherParties([...otherParties,{
                                                                state:"external",
                                                                value:externalPartySelectedForOtherParties
                                                            }]);
                                                        }
                                                        setAddOtherPartyButtonToggle(false)
                                                        setInternalExternalRadioButton("");
                                                        setInternalPartySelectedForOtherParties("");
                                                        setExternalPartySelectedForOtherParties("")
                                                    }}
                                                >
                                                    <i className="bi bi-check-lg"></i>
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="formBoxRow p-t-5 p-b-5">
                                        <div className="formBoxLabel fs-14">
                                            {trans.fetchLabelKeyTranslation("CallTimeField","Call Time (UTC)")}*
                                        </div>
                                        <div className="formBoxAction">
                                            <div className="formInput">
                                                <Field
                                                id="calltimeUtc"
                                                name="calltimeUtc"
                                                style={{ height: "32px" }}
                                                value={formRenderProps.valueGetter("calltimeUtc")}
                                                component={DateTimePicker}
                                                calendar={CustomCalendar}
                                                placeholder={trans.fetchLabelKeyTranslation("CallTimePlaceholder","Enter call time")}
                                                validator={CallTimeValidator}
                                                max={new Date()}
                                                format={{
                                                    skeleton: 'yMMMdEHmSs'
                                                }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="formBoxRow p-t-5 p-b-5">
                                        <div className="formBoxLabel fs-14">
                                            {trans.fetchLabelKeyTranslation("CallDirectionField","Call Direction")}*
                                        </div>
                                        <div className="formBoxAction">
                                            <div className="formInput">
                                                <Field
                                                id="callDirection"
                                                name="callDirection"
                                                style={{ height: "32px" }}
                                                value={formRenderProps.valueGetter("callDirection")}
                                                component={CustomComboBox}
                                                placeholder={trans.fetchLabelKeyTranslation("CallDirectionPlaceholder","Choose call direction")}
                                                data={masterData?.data?.callDirections}
                                                textField="localizationValue"
                                                validator={CallDirectionValidator}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="formBoxRow p-t-5 p-b-5">
                                        <div className="formBoxLabel fs-14">
                                        {trans.fetchLabelKeyTranslation("PlatformCallIdField","Platform Call ID")}
                                        </div>
                                        <div className="formBoxAction">
                                            <div className="formInput">
                                                <Field
                                                id="platformCallId"
                                                name="platformCallId"
                                                style={{ height: "32px" }}
                                                value={formRenderProps.valueGetter("platformCallId")}
                                                component={CustomInput}
                                                placeholder={trans.fetchLabelKeyTranslation("PlatformCallIdPlaceholder","Enter Platform Call ID")}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="formBoxRow p-t-5 p-b-5">
                                        <div className="formBoxLabel fs-14">
                                        {trans.fetchLabelKeyTranslation("PlatformCallDetailIdField","Platform Call Detail Id")}
                                        </div>
                                        <div className="formBoxAction">
                                            <div className="formInput">
                                                <Field
                                                id="platformCallDetailId"
                                                name="platformCallDetailId"
                                                style={{ height: "32px" }}
                                                value={formRenderProps.valueGetter("platformCallDetailId")}
                                                component={CustomInput}
                                                placeholder={trans.fetchLabelKeyTranslation("PlatformCallDetailIdPlaceholder","Enter Plaform Call Detail ID")}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <span className='fs-14 tx-red'>{error}</span><br/>
                                <Button className={`btn bg-black-5`} onClick={onCloseDialog}>
                                    {trans.fetchLabelKeyTranslation("CancelBtnText","Cancel")}
                                </Button>
                                <Button
                                    className={` ${
                                    "m-l-10 k-button k-button-md k-rounded-md k-button-solid k-button-solid-base bg-primary text-white" +
                                    (!formRenderProps.valid || loader || otherParties.length === 0
                                        ? "disabledBtn"
                                        : "")
                                    }`}
                                    onClick={submitHandler}
                                    disabled={!formRenderProps.valid || loader || otherParties.length===0}
                                >
                                    {trans.fetchLabelKeyTranslation("SubmitBtnText","Submit")}
                                </Button>
                            </FormElement>
                        )}
                    />
            </div>
            )}
        </div>
    )
}

export default UploadRecording;